import React, { useState, useEffect } from 'react';
import { Link, useParams, useHistory } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useGlobal } from 'reactn';

import { useApi } from 'stores/api';
import { ATTACHMENT_TYPE, BOOKING_STATUS_TYPE, SERVICE_TYPE } from 'stores/const';
import { fmtPrice, fmtDatetime, fmtDate, fmtPercent } from 'stores/utils';
import { Focuspoint } from 'components/Focuspoint';

const BookingDetails = (props) => {

    const { t } = useTranslation(['user-bookingdetails']);
    let history = useHistory();
    const api = useApi();
    let { bookingId } = useParams();
    const [modalProps, setModalProps] = useGlobal('modalProps');
    const [details, setDetails] = useState(null);
    const [rentalObject, setRentalObject] = useState(null);
    const [firstImage, setFirstImage] = useState(null);

    const initFirstImage = (rentalObject) => {
        let first = rentalObject.RentalObjectAttachment.find(item => item.refRentalObjectAttachmentTypeId === ATTACHMENT_TYPE.IMAGE);
        if (first) {
            setFirstImage(first);
        }
    }

    const showCancelModal = async () => {
        let cancellationFeeInfo = await api.getCancellationFee(bookingId);
        setModalProps({
            ...modalProps,
            showModal: true,
            showCancel: true,
            okLabel: t("btnCancelBooking"),
            cancelLabel: t("btnKeepBooking"),
            messages: [t("cancelBookingMessage"), cancellationFeeInfo.MessageWarn],
            isDeletion: true,
            onOk: cancelBooking
        });
    }

    const cancelBooking = async () => {
        let ffStatus = details.RentalObjectFulfillmentTypeStatus;
        if (ffStatus && ffStatus.length > 0 && ffStatus[0].RentalObjectFulfillmentTypeStatusTransition) {
            let fulfillmentStatuses = ffStatus[0].RentalObjectFulfillmentTypeStatusTransition;
            if (fulfillmentStatuses && fulfillmentStatuses.length > 0) {
                let cancelStatus = fulfillmentStatuses.find(item => item.refRentalTransactionStatusId === BOOKING_STATUS_TYPE.CANCELLED);
                if (cancelStatus) {
                    let ffStatusCancelId = cancelStatus.refRentalTransactionFulfillmentStatusId;
                    console.log('ffStatusCancelId:' + ffStatusCancelId);
                    const bookingDetails = await api.cancelBooking(bookingId, ffStatusCancelId);
                    setBookingDetails(bookingDetails);
                }
            }
        }
    }

    const setBookingDetails = (bookingDetails) => {
        let mainObjectOffers = bookingDetails.MainObjectOffers.RentalTransactionOffer;
        let accessoryOffers = bookingDetails.AccessoryObjectOffers.RentalTransactionOffer;
        let servicesConditioned = bookingDetails.ConditionalServices.RentalTransactionService;
        let servicesUnconditioned = bookingDetails.ServicesWithoutConditional.RentalTransactionService;

        let mainObjectSubtotal = bookingDetails.MainObjectOffers.SubtotalAmount;
        let accOffersSubtotal = bookingDetails.AccessoryObjectOffers.SubtotalAmount;
        let subtotalMainObjectAndAccessories = 0;
        if (mainObjectSubtotal && accOffersSubtotal) {
            subtotalMainObjectAndAccessories = parseFloat(mainObjectSubtotal) + parseFloat(accOffersSubtotal);
        }
        let subtotalServicesConditioned = bookingDetails.ConditionalServices.SubtotalAmount;
        let subtotalServicesUnconditioned = bookingDetails.ServicesWithoutConditional.SubtotalAmount;
        let consumables = bookingDetails.Consumables.RentalTransactionConsumable;
        let subtotalConsumables = bookingDetails.Consumables.SubtotalAmount;
        let extras = bookingDetails.RentalTransactionAdHoc;
        let subtotalExtras = extras?.reduce((a, b) => a + parseFloat(b.price), 0);
        let cancellationFees = bookingDetails.RentalTransactionCancellationFee;
        let payments = bookingDetails.Payments?.RentalTransactionPayment;
        let cancelHours = bookingDetails.RentalTransactionCancellationFee?.map(fee => fee.MinHoursPriorToPickup).map(Number);
        let cancelTimeunit = Math.max.apply(Math, cancelHours) >= 48 ? 'DAYS' : 'HOURS';

        setDetails({
            ...bookingDetails, mainObjectOffers, accessoryOffers, servicesConditioned, servicesUnconditioned,
            mainObjectSubtotal, accOffersSubtotal, subtotalMainObjectAndAccessories,
            subtotalServicesConditioned, subtotalServicesUnconditioned, consumables, subtotalConsumables,
            extras, subtotalExtras, cancelTimeunit, cancellationFees, payments
        });
    }

    const initBookingDetails = async () => {
        const bookingDetails = await api.fetchBookingDetails(bookingId);
        const objectId = bookingDetails.refRentalObjectId;
        const objDetails = await api.fetchObjectDetailsMin(objectId);
        setRentalObject(objDetails);
        initFirstImage(objDetails);

        setBookingDetails(bookingDetails);
    }

    const getStatusClass = (details) => {
        const status = details.refRentalTransactionStatusId;
        if (status === BOOKING_STATUS_TYPE.BOOKED) return ' booked';
        else if (status === BOOKING_STATUS_TYPE.CANCELLED) return ' cancelled';
        else if (status === BOOKING_STATUS_TYPE.INTERRUPTED) return ' interrupted';
        return '';
    }

    const isCancellable = () => {
        if (details.refRentalTransactionStatusId === BOOKING_STATUS_TYPE.BOOKED) {
            const currentTime = new Date().getTime();
            const rentingStartTime = new Date(details.RentingStartDate).getTime();
            return currentTime < rentingStartTime;
        }
        return false;
    }

    useEffect(() => {
        initBookingDetails();
    }, [])

    return (
        <>
            {
                details &&
                <>
                    <section className="my-bookings-filter-container sticky-container">
                        <div className="filter-header-wrapper">
                            <div className="wrapper">
                                <div className="row">
                                    <div className="column-4 filter-leftAlign">
                                        <Link to="/user/bookings" className="back">
                                            <i className="fa fa-arrow-left" aria-hidden="true"></i> {t("back")}
                                        </Link>
                                    </div>
                                    <div className="column-8 filter-rightAlign">
                                        <a href={`/api/Lessee/RentalTransaction/${bookingId}/BillReport`}
                                            target="_blank" className="print">
                                            {t("performPrint")} <i className="fa fa-print" aria-hidden="true"></i>
                                        </a>
                                        {isCancellable() &&
                                            <a onClick={showCancelModal} className="cancel">
                                                {t("performCancel")} <i className="fa fa-times-circle" aria-hidden="true"></i>
                                            </a>
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className="my-bookings-container">
                        <div className="wrapper">
                            <div className="main-details-container">

                                <div className="row">
                                    <div className="column-8">
                                        <div className="product-container">
                                            <figure className="product-image">
                                                <div className="spacer"></div>
                                                {firstImage && <Focuspoint
                                                    src={`/lessors/DBImg.ashx?id=${firstImage.Id}`}
                                                    imageW={756}
                                                    imageH={415}
                                                    focusX={0.0}
                                                    focusY={0.0}
                                                    reCalcOnWindowResize={true}
                                                />}
                                            </figure>

                                            <div className="product-details">
                                                <div className="product-name biggest">
                                                    {details.Name}
                                                </div>
                                                <div className="product-total">
                                                    <div className="label">{t("mietbetrag")}</div>
                                                    <div className="amount number">{details.Currency_Name} {fmtPrice(details.TotalDiscountedPrice)}{" "}
                                                        <span className="amount-note">{t("vatIncluded")}</span>
                                                    </div>
                                                    <div className="price-detail-trigger">
                                                        <a><i className="fa fa-info" aria-hidden="true"></i></a>
                                                    </div>
                                                </div>
                                                <div className="product-location">
                                                    <i className="fa fa-map-marker" aria-hidden="true"></i> {rentalObject.BusinessUnit_Name}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="column-4">
                                        <div className="bookingdates-container">
                                            <div className="booking-center">
                                                <div className="from">
                                                    <div className="label">{t("bookedFrom")}</div>
                                                    <div className="value">{fmtDatetime(new Date(details.RentingStartDate.substring(0, 19)))}</div>
                                                </div>
                                                <div className="till">
                                                    <div className="label">{t("bookedTo")}</div>
                                                    <div className="value">{fmtDatetime(new Date(details.RentingEndDate.substring(0, 19)))}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="column-12">
                                        <div className="bookingdetails-container">
                                            <div className="column-4">
                                                <div className="label">{t("bookingNumber")}</div>
                                                <div className="value">{details.Number}</div>
                                            </div>
                                            <div className="column-4">
                                                <div className="label">{t("bookingState")}</div>
                                                <div className={`value status${getStatusClass(details)}`}>{details.RentalTransactionStatus_Name}</div>
                                            </div>
                                            <div className="column-4">
                                                <div className="label">{t("bookingSaldo")}</div>
                                                <div className={"value number" + (parseFloat(details.PaymentBalance) > 0 ? " negative" : "")}>
                                                    {details.Currency_Name} {fmtPrice(details.PaymentBalance)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {details.mainObjectOffers.length > 0 && <div className="row with-table">
                                    <div className="column-12">
                                        <div className="title">{t("objectAndAccessories")}</div>
                                        <div className="table">
                                            <div className="tableheader">
                                                <div className="tabledata column-4">
                                                    {t("description")}
                                                </div>
                                                <div className="tabledata column-2 center">
                                                    {t("count")}
                                                </div>
                                                <div className="tabledata column-2 center">
                                                    {t("offer")}
                                                </div>
                                                <div className="tabledata column-2 center">
                                                    {t("season")}
                                                </div>
                                                <div className="tabledata column-2 right">
                                                    {t("price")}
                                                </div>
                                            </div>
                                            {details.mainObjectOffers.map(offer => {
                                                return (
                                                    <div className="tablerow" key={offer.Id}>
                                                        <div className="tabledata column-4">
                                                            {offer.RentalObjectName}
                                                            {offer.ProlongationOfferQuantity > 0 &&
                                                                <div className="prolongation">
                                                                    {offer.ProlongationName}
                                                                </div>}
                                                        </div>
                                                        <div className="tabledata column-2 center">
                                                            {offer.RentalObjectQuantity > 0 ? offer.RentalObjectQuantity : "-"}
                                                            {offer.ProlongationOfferQuantity > 0 &&
                                                                <div className="prolongation">
                                                                    {offer.ProlongationOfferQuantity}
                                                                </div>}
                                                        </div>
                                                        <div className="tabledata column-2 center">
                                                            {offer.Name}
                                                        </div>
                                                        <div className="tabledata column-2 center">
                                                            {offer.SeasonName}
                                                        </div>
                                                        <div className="tabledata column-2 right number">
                                                            {details.Currency_Name}&nbsp;{fmtPrice(offer.TotalPrice)}
                                                        </div>
                                                    </div>
                                                )
                                            })}

                                            {details.accessoryOffers?.map(offer => {
                                                return (
                                                    <div className="tablerow" key={offer.Id}>
                                                        <div className="tabledata column-4">
                                                            {offer.RentalObjectName}
                                                        </div>
                                                        <div className="tabledata column-2 center">
                                                            {offer.RentalObjectQuantity > 0 ? offer.RentalObjectQuantity : "-"}
                                                        </div>
                                                        <div className="tabledata column-2 center">
                                                            {offer.Name}
                                                        </div>
                                                        <div className="tabledata column-2 center">
                                                            {offer.SeasonName}
                                                        </div>
                                                        <div className="tabledata column-2 right number">
                                                            {details.Currency_Name}&nbsp;{fmtPrice(offer.TotalPrice)}
                                                        </div>
                                                    </div>
                                                )
                                            })}

                                            <div className="tablefooter total-cost">
                                                <div className="tabledata column-6">
                                                    {t("subTotal")}
                                                </div>
                                                <div className="tabledata column-6 right number">
                                                    {details.Currency_Name}&nbsp;{fmtPrice(details.subtotalMainObjectAndAccessories)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                }

                                {details.servicesConditioned?.length > 0 && <div className="row with-table">
                                    <div className="column-12">
                                        <div className="title">{t("conditionalServices")}</div>
                                        <div className="table">
                                            <div className="tableheader">
                                                <div className="tabledata column-4">
                                                    {t("article")}
                                                </div>
                                                <div className="tabledata column-6">
                                                    {t("description")}
                                                </div>
                                                <div className="tabledata column-2 right">
                                                    {t("price")}
                                                </div>
                                            </div>
                                            {details.servicesConditioned.map(service => {
                                                return (
                                                    <div className={"tablerow" + (service.ResultPrice < 0 ? " discount" : "")} key={service.Id}>
                                                        <div className="tabledata column-4">
                                                            {service.Name}
                                                        </div>
                                                        <div className="tabledata column-6">
                                                            {service.Description}
                                                        </div>
                                                        <div className="tabledata column-2 right number">
                                                            {details.Currency_Name}&nbsp;{fmtPrice(service.ResultPrice)}
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                            <div className="tablefooter total-cost">
                                                <div className="tabledata column-6">
                                                    {t("subtotal")}
                                                </div>
                                                <div className="tabledata column-6 right number">
                                                    {details.Currency_Name} {fmtPrice(details.subtotalServicesConditioned)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                }

                                {details.servicesUnconditioned?.length > 0 &&
                                    <div className="row with-table">
                                        <div className="column-12">
                                            <div className="title">{t("services")}</div>
                                            <div className="table">
                                                <div className="tableheader">
                                                    <div className="tabledata column-4">
                                                        {t("article")}
                                                    </div>
                                                    <div className="tabledata column-6">
                                                        {t("description")}
                                                    </div>
                                                    <div className="tabledata column-2 right">
                                                        {t("price")}
                                                    </div>
                                                </div>
                                                {details.servicesUnconditioned.map(service => {
                                                    return (
                                                        <div className={"tablerow" + (service.ResultPrice < 0 ? " discount" : "")} key={service.Id}>
                                                            <div className="tabledata column-4">
                                                                {service.Name} {(service.ResultPrice < 0 && service.refRentalServiceTypeId === SERVICE_TYPE.OBLIGATORY) && <span>{t("discount")}</span>}
                                                            </div>
                                                            <div className="tabledata column-6">
                                                                {service.Description}
                                                            </div>
                                                            <div className="tabledata column-2 right number">
                                                                {details.Currency_Name}&nbsp;{fmtPrice(service.ResultPrice)}
                                                            </div>
                                                        </div>
                                                    )
                                                })}
                                                <div className="tablefooter total-cost">
                                                    <div className="tabledata column-6">
                                                        {t("subtotal")}
                                                    </div>
                                                    <div className="tabledata column-6 right number">
                                                        {details.Currency_Name} {fmtPrice(details.subtotalServicesUnconditioned)}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                }

                                {details.Deposit &&
                                    <div className="row with-table">
                                        <div className="column-12">
                                            <div className="title">{t("deposit")}</div>
                                            <div className="table">
                                                <div className={"tablerow"}>
                                                    <div className="tabledata column-6">
                                                        {t("deposit")}
                                                    </div>
                                                    <div className="tabledata column-6 right number">
                                                        {details.Currency_Name}&nbsp;{fmtPrice(details.Deposit)}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                }

                                {details.consumables?.length > 0 && <div className="row with-table">
                                    <div className="column-12">
                                        <div className="title">{t("consumables")}</div>
                                        <div className="table">
                                            <div className="tableheader">
                                                <div className="tabledata column-4">
                                                    {t("consumableName")}
                                                </div>
                                                <div className="tabledata column-2 center">
                                                    {t("unit")}
                                                </div>
                                                <div className="tabledata column-2 center">
                                                    {t("minIncl")}
                                                </div>
                                                <div className="tabledata column-2 center">
                                                    {t("pricePerUnit")}
                                                </div>
                                                <div className="tabledata column-2 right">
                                                    {t("price")}
                                                </div>
                                            </div>
                                            {details.consumables.map(consumable => {
                                                return (
                                                    <div className="tablerow">
                                                        <div className="tabledata column-4">
                                                            {consumable.Name}
                                                        </div>
                                                        <div className="tabledata column-2 center">
                                                            {consumable.Unit}
                                                        </div>
                                                        <div className="tabledata column-2 center number">
                                                            {parseFloat(consumable.MinQuantity).toFixed(2)} / {parseFloat(consumable.FreeUnitQuantitySum).toFixed()}
                                                        </div>
                                                        <div className="tabledata column-2 center number">
                                                            {fmtPrice(consumable.pricePerUnit)}
                                                        </div>
                                                        <div className="tabledata column-2 number right number">
                                                            {fmtPrice(consumable.Price)}
                                                        </div>
                                                    </div>
                                                )
                                            })}

                                            <div className="tablefooter total-cost">
                                                <div className="tabledata column-6">
                                                    {t("subtotal")}
                                                </div>
                                                <div className="tabledata column-6 right number">
                                                    {details.Currency_Name} {fmtPrice(details.subtotalConsumables)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                }

                                {details.extras?.length > 0 && <div className="row with-table">
                                    <div className="column-12">
                                        <div className="title">{t("extras")}</div>
                                        <div className="table">
                                            <div className="tableheader">
                                                <div className="tabledata column-10">
                                                    {t("priceShiftName")}
                                                </div>
                                                <div className="tabledata column-2 right">
                                                    {t("price")}
                                                </div>
                                            </div>
                                            {details.extras.map(extra =>
                                                <div className={"tablerow" + (extra.Amount < 0 ? " discount" : "")}>
                                                    <div className="tabledata column-10">
                                                        {extra.DescriptionNote}
                                                    </div>
                                                    <div className="tabledata column-2 number right number">
                                                        {fmtPrice(extra.Amount)}
                                                    </div>
                                                </div>
                                            )}
                                            <div className="tablefooter total-cost">
                                                <div className="tabledata column-6">
                                                    {t("subTotal")}
                                                </div>
                                                <div className="tabledata column-6 right number">
                                                    {details.Currency_Name}&nbsp;{fmtPrice(details.subtotalExtras)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>}

                                {details.cancellationFees?.length > 0 && <div className="row with-table">
                                    <div className="column-12">
                                        <div className="title">{t("stornoGebuhren")}</div>
                                        <div className="table">
                                            <div className="tableheader">
                                                <div className="tabledata column-4">
                                                    {details.cancelTimeunit=='DAYS' ? t("daysToPickup") : t("hoursToPickup")}
                                                </div>
                                                <div className="tabledata column-4 right">
                                                    {t("percent")}
                                                </div>
                                                <div className="tabledata column-4 right">
                                                    {t("fix")}
                                                </div>
                                            </div>
                                            {details.cancellationFees.map(fee => {
                                                return (
                                                    <div className="tablerow" key={`${fee.MinHoursPriorToPickup}_${fee.Rate}_${fee.Fix}`}>
                                                        <div className="tabledata column-4">
                                                            {details.cancelTimeunit=='DAYS' ? fee.MinHoursPriorToPickup/24 : fee.MinHoursPriorToPickup }
                                                        </div>
                                                        <div className="tabledata column-4 right number">
                                                            {fmtPercent(fee.Rate)}%
                                                        </div>
                                                        <div className="tabledata column-4 right number">
                                                            {fmtPrice(fee.Fix)}
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    </div>
                                </div>
                                }

                                {details.TotalDiscountPrice < 0 &&
                                    <div className="row with-table">
                                        <div className="column-12">
                                            <div className="title">{t("totalDiscount")}</div>
                                            <div className="table">
                                                <div className="tablerow discount">
                                                    <div className="tabledata column-6">
                                                        {t("discount")}
                                                    </div>
                                                    <div className="tabledata column-6 right number">
                                                        {details.Currency_Name}&nbsp;{fmtPrice(details.TotalDiscountPrice)}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                }

                                <div className="row with-table">
                                    <div className="column-12">
                                        <div className="table final-cost">
                                            <div className="tablerow">
                                                <div className="tabledata title column-9">
                                                    {t("bookingTotalPrice")}
                                                </div>
                                                <div className="tabledata column-3 number right number">
                                                    {details.Currency_Name} {fmtPrice(details.SumChargeAmount)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="row with-table">
                                    <div className="column-12">
                                        <div className="title">{t("billingAmount")}</div>
                                        <div className="table">
                                            <div className="tablerow main-payment">
                                                <div className="tabledata text column-9">
                                                    {t("gesamtbetragFallig")} {fmtDate(details.FullpaymentDueDate)}
                                                </div>
                                                <div className="tabledata column-3 right number">
                                                    {details.Currency_Name} {fmtPrice(details.SumChargeAmount)}
                                                </div>
                                            </div>
                                            <div className="tablerow tax">
                                                <div className="tabledata text column-9">
                                                    {t("enthalteneMwst", { vatPercentage: fmtPercent(details.Vat.VatRate) })}
                                                </div>
                                                <div className="tabledata column-3 right number">
                                                    {details.Currency_Name} {fmtPrice(details.Vat.sumChargeVatAmount)}
                                                </div>
                                            </div>
                                            <div className="tablerow">
                                                <div className="tabledata column-9">
                                                    {t("anzahlungFallig")} {fmtDate(details.DownpaymentDueDate)}
                                                </div>
                                                <div className="tabledata column-3 right number">
                                                    {details.Currency_Name}&nbsp;{fmtPrice(details.DownpaymentAmount)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {details.payments?.length > 0 && <div className="row with-table">
                                    <div className="column-12">
                                        <div className="title">{t("receivedPayments")}</div>
                                        <div className="table">
                                            {details.payments.map(payment => {
                                                return (
                                                    <div className="tablerow">
                                                        <div className="tabledata column-2 number">
                                                            {fmtDate(payment.Date)}
                                                        </div>
                                                        <div className="tabledata column-7">
                                                            {payment.Description}
                                                        </div>
                                                        <div className="tabledata column-3 right number">
                                                            {details.Currency_Name} {fmtPrice(payment.PaymentAmount)}
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    </div>
                                </div>
                                }
                            </div>
                        </div>
                    </section>
                </>
            }
        </>
    );
}

export default BookingDetails;